import { useId } from 'react';
import { useElementWidth } from '../hooks/useElementWidth';
import '../css/Pos.css';

/** Below this the chart is on a phone and drops to its compact settings. */
const NARROW = 520;
/** Above this many points the dots merge into the line, so they are dropped. */
const MARKER_LIMIT = 24;
/** Past this many lines a lone peak label cannot say which line it belongs to. */
const PLAIN_SERIES_LIMIT = 1;
/**
 * Opacity of the wash under a line — lighter once there are several, because
 * washes stack where categories overlap and two at full strength read as a
 * third colour the palette never had.
 */
const FILL_OPACITY_SOLO = 0.26;
const FILL_OPACITY_MULTI = 0.15;
/**
 * Past this many lines the end labels stack into an unreadable column, and
 * the legend is left to carry identity on its own.
 */
const END_LABEL_LIMIT = 6;
/** Width reserved to the right of the plot for those labels. */
const END_LABEL_GUTTER = 88;
/** Least vertical space between two end labels before they are pushed apart. */
const END_LABEL_MIN_GAP = 13;
/** Characters of category name an end label shows before truncating. */
const END_LABEL_CHARS = 11;

/**
 * Rounds an axis maximum up to something a person would choose.
 *
 * A raw maximum puts the top gridline at ₱4,837, which nobody reads as a
 * scale. This walks up to the next 1 / 2 / 2.5 / 5 × 10^n instead.
 *
 * @param {number} value - the largest plotted value
 * @returns {number} a rounder ceiling, never 0
 */
const niceCeiling = (value) => {
  if (!(value > 0)) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find(s => value <= s * magnitude) || 10;
  return step * magnitude;
};

/**
 * Takings over the report period, as one line or several.
 *
 * A line rather than ranked bars because the question is change over time,
 * where the shape between the points — the climb, the dip, the flat weekend —
 * is the finding, and only a line draws that.
 *
 * Deliberately dumb about what it is plotting: it takes ready-made series and
 * a formatter, so the screen's Amount/Quantity and Overall/Category toggles
 * are decisions made once in PosSales rather than four branches in here.
 *
 * Drawn at the container's MEASURED width with a fixed pixel height, not
 * scaled from a fixed viewBox. A scaled chart takes its type and stroke widths
 * with it, so the axis labels that read correctly on a laptop would come out
 * at about 5px on a phone. Here the geometry is recomputed per width instead.
 *
 * Hand-rolled SVG rather than a charting library, matching the rest of this
 * app: one chart does not justify a dependency, and this way it inherits the
 * theme directly.
 *
 * A legend appears as soon as there are two lines, so identity is never
 * carried by colour alone; with one line the heading already names it.
 *
 * @param {object} props
 * @param {Array<{key: string, label: string}>} props.points - the x axis periods
 * @param {Array<{key: string, label: string, color: string, values: number[]}>} props.series
 *   one entry per line; `values` is parallel to `points`
 * @param {(value: number) => string} props.formatValue - axis and tooltip formatter
 * @param {string[]} [props.tooltips] - one line of hover text per period,
 *   parallel to `points`; falls back to a per-series list when absent
 * @param {string} [props.summary] - the chart's accessible description
 * @param {(index: number) => string} [props.peakNote] - extra text printed after
 *   the peak's value (e.g. its cost), for the period at that index; '' for none
 * @returns {JSX.Element|null}
 */
function PosSalesTrendChart({
  points, series, formatValue, tooltips = null, summary = '', peakNote = null,
}) {
  const [wrapRef, width] = useElementWidth();
  // Gradient ids have to be unique in the DOCUMENT, not just this component —
  // two charts on one page sharing an id would both paint the first one's
  // colour. useId gives a per-instance prefix without a module counter that
  // would drift between renders.
  //
  // Stripped of punctuation because React's ids carry delimiters (':r0:' on
  // 18, '«r0»' on 19) and those are not legal inside a url(#...) reference —
  // the fill would silently resolve to nothing and the wash would vanish.
  const gradientId = `postrend${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  // The ref still has to be attached on the empty render, or the container is
  // never measured and the chart stays at its fallback width once data lands.
  if (!points?.length || !series?.length) return <div ref={wrapRef} className="pos-chart-wrap" />;

  const narrow = width < NARROW;
  // Taller on a phone than proportional scaling would have given: a 360px
  // chart scaled from a 720×220 box is 110px tall, which flattens the very
  // shape the line exists to show.
  const height = narrow ? 190 : 250;
  const padL = narrow ? 46 : 58;     // room for the value labels
  // Each line named at its own end, so identity does not depend on matching a
  // colour to a legend below the chart. Skipped on a phone, where the gutter
  // would cost a third of the plot, and past END_LABEL_LIMIT lines, where the
  // labels stack into a column of their own — the legend covers both cases.
  const showEndLabels = series.length > 1 && series.length <= END_LABEL_LIMIT && !narrow;
  const padR = showEndLabels ? END_LABEL_GUTTER : 14;
  const padT = 16;
  const padB = 26;                   // room for the period labels
  const gridLines = narrow ? 3 : 4;
  const maxXLabels = narrow ? 4 : 7;

  const plotW = Math.max(40, width - padL - padR);
  const plotH = height - padT - padB;
  // One scale for every line — never an axis per series, which would let two
  // lines of different magnitude appear to cross when they never do.
  const max = niceCeiling(Math.max(...series.flatMap(s => s.values), 0));

  // A single period would otherwise divide by zero; it is drawn as one dot in
  // the middle of the plot rather than a line with nowhere to go.
  const stepX = points.length > 1 ? plotW / (points.length - 1) : 0;
  const xAt = (i) => (points.length > 1 ? padL + i * stepX : padL + plotW / 2);
  const yAt = (value) => padT + plotH - (Math.max(0, value) / max) * plotH;

  const plain = series.length <= PLAIN_SERIES_LIMIT;
  const showMarkers = points.length <= MARKER_LIMIT && series.length <= 3;
  // Thinned to whole steps so the labels that survive stay evenly spaced.
  const labelEvery = Math.max(1, Math.ceil(points.length / maxXLabels));
  const gridValues = Array.from({ length: gridLines + 1 }, (_, i) => (max / gridLines) * i);

  /**
   * One line's path, and the closed version used for its area wash.
   *
   * @param {number[]} values - parallel to `points`
   * @returns {{line: string, area: string}}
   */
  const pathsFor = (values) => {
    const line = values
      .map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`)
      .join(' ');
    const area = `${line} L${xAt(values.length - 1).toFixed(1)},${padT + plotH}`
      + ` L${xAt(0).toFixed(1)},${padT + plotH} Z`;
    return { line, area };
  };

  // Built once and read by both passes below, so the path strings are not
  // rebuilt a second time just to draw the washes.
  const geometry = series.map(s => ({ ...s, ...pathsFor(s.values) }));

  /**
   * Where each line's name sits, nudged apart so two lines ending at the same
   * value do not print their labels on top of each other.
   *
   * Walks the labels in y order pushing each below the one before, then
   * shifts the whole column back up if that pushed the last one out of the
   * plot — which keeps the set together rather than clipping the bottom name.
   */
  const endLabels = (() => {
    if (!showEndLabels) return [];
    const last = points.length - 1;
    const rows = geometry
      .map(s => ({ key: s.key, label: s.label, color: s.color, y: yAt(s.values[last]) }))
      .sort((a, b) => a.y - b.y);

    for (let i = 1; i < rows.length; i += 1) {
      if (rows[i].y - rows[i - 1].y < END_LABEL_MIN_GAP) {
        rows[i].y = rows[i - 1].y + END_LABEL_MIN_GAP;
      }
    }
    const spill = rows.length ? rows[rows.length - 1].y - (padT + plotH) : 0;
    if (spill > 0) rows.forEach((r) => { r.y -= spill; });
    return rows;
  })();

  // Named once, and only with a single line — on several, a lone number
  // hanging over the plot does not say which line it belongs to.
  const peakIndex = plain
    ? series[0].values.reduce((best, v, i) => (v > series[0].values[best] ? i : best), 0)
    : -1;

  // Where the peak label hangs from. Centred, as before, unless a note makes
  // it long enough to spill past the plot near either end.
  const hasPeakNote = peakIndex >= 0 && Boolean(peakNote?.(peakIndex));
  let peakAnchor = 'middle';
  if (hasPeakNote && xAt(peakIndex) > width - padR - 90) peakAnchor = 'end';
  else if (hasPeakNote && xAt(peakIndex) < padL + 90) peakAnchor = 'start';

  /**
   * The hover text for one period: the caller's line if it supplied one,
   * otherwise each series and its value.
   *
   * @param {number} i - period index
   * @returns {string}
   */
  const tooltipAt = (i) => (
    tooltips?.[i]
    || `${points[i].label}\n${series.map(s => `${s.label}: ${formatValue(s.values[i])}`).join('\n')}`
  );

  return (
    <div ref={wrapRef} className="pos-chart-wrap">
      <svg
        className="pos-trend"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={summary}
      >
        <defs>
          {/* One wash per line, in that line's own colour. Carries no data of
              its own — it gives each line a body so a mostly-flat series reads
              as a shape rather than a scratch across the panel. */}
          {series.map((s, i) => (
            <linearGradient key={s.key} id={`${gradientId}-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={plain ? FILL_OPACITY_SOLO : FILL_OPACITY_MULTI} />
              <stop offset="100%" stopColor={s.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {/* Recessive by design: the grid is scaffolding for reading the lines,
            not a thing to look at. */}
        {gridValues.map((value) => {
          const y = yAt(value);
          return (
            <g key={value}>
              <line x1={padL} y1={y} x2={width - padR} y2={y} className="pos-trend-grid" />
              <text x={padL - 8} y={y + 3} className="pos-trend-axis" textAnchor="end">
                {formatValue(value)}
              </text>
            </g>
          );
        })}

        {/* EVERY wash first, then every line. Drawn per-series instead, the
            second category's fill would wash over the first category's stroke
            — and the crossings are exactly what this view exists to show. */}
        {geometry.map((s, i) => (
          <path key={`area-${s.key}`} d={s.area} fill={`url(#${gradientId}-${i})`} />
        ))}

        {geometry.map((s, si) => {
          const { line } = s;
          return (
            <g key={s.key}>
              <path d={line} className="pos-trend-line" stroke={s.color} />
              {showMarkers && s.values.map((v, i) => (
                <circle
                  key={`${s.key}-${points[i].key}`}
                  cx={xAt(i)}
                  cy={yAt(v)}
                  r={narrow ? 3 : 4}
                  fill={s.color}
                  className="pos-trend-dot"
                />
              ))}
              {si === 0 && peakIndex >= 0 && (
                // Kept inside the plot so a peak at either end does not hang
                // off the edge. With a note the label is longer, so near an
                // edge it is anchored to that side instead of centred.
                <text
                  x={peakAnchor === 'middle'
                    ? Math.min(Math.max(xAt(peakIndex), padL + 22), width - padR - 22)
                    : xAt(peakIndex)}
                  y={Math.max(yAt(s.values[peakIndex]) - 10, padT + 8)}
                  className="pos-trend-peak"
                  textAnchor={peakAnchor}
                >
                  {formatValue(s.values[peakIndex])}
                  {peakNote && peakNote(peakIndex) && (
                    <tspan className="pos-trend-peak-note">{` · ${peakNote(peakIndex)}`}</tspan>
                  )}
                </text>
              )}
            </g>
          );
        })}

        {/* The line's name, at the line's end. A colour dot carries identity
            and the text stays in ink — a whole label in the series colour
            reads as decoration and loses contrast on the lighter slots. */}
        {endLabels.map((r) => (
          <g key={`end-${r.key}`}>
            <circle cx={xAt(points.length - 1) + 9} cy={r.y} r={3.5} fill={r.color} />
            <text x={xAt(points.length - 1) + 17} y={r.y + 3.5} className="pos-trend-endlabel">
              {r.label.length > END_LABEL_CHARS ? `${r.label.slice(0, END_LABEL_CHARS - 1)}…` : r.label}
            </text>
          </g>
        ))}

        {points.map((p, i) => (
          (i % labelEvery === 0 || i === points.length - 1) && (
            <text
              key={`x-${p.key}`}
              x={xAt(i)}
              y={height - 8}
              className="pos-trend-axis"
              textAnchor={i === 0 ? 'start' : (i === points.length - 1 ? 'end' : 'middle')}
            >
              {p.label}
            </text>
          )
        ))}

        {/* Hit areas, a full column each so a finger does not have to find a
            4px dot. Native <title> tooltips rather than a hand-built crosshair
            layer — the same affordance the rest of this app's charts use, and
            it works with touch and the keyboard. */}
        {points.map((p, i) => (
          <rect
            key={`hit-${p.key}`}
            x={xAt(i) - (stepX || plotW) / 2}
            y={padT}
            width={stepX || plotW}
            height={plotH}
            className="pos-trend-hit"
          >
            <title>{tooltipAt(i)}</title>
          </rect>
        ))}
      </svg>

      {/* Present from the second line onwards, so identity never rests on
          colour alone. A single line needs none — the heading names it. */}
      {series.length > 1 && (
        <ul className="pos-trend-legend">
          {series.map(s => (
            <li key={s.key}>
              <span className="pos-trend-swatch" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PosSalesTrendChart;
